import { ReactElement, useMemo } from 'react';
import { CircularProgress, Stack, Typography } from '@mui/material';
import { useFormikContext } from 'formik';

import {
  StepperGenerateRoute,
  GenerateRouteCompletedTabs,
} from '~components/index';
import { isCompletedWorkProgressId } from '~components/StepperGenerateRoute/utils';
import { useGenerateRouteStepperData, useUtcOffset } from '~hooks/index';

import { SCHEDULE_ROUTE_BASE_FORM_FIELDS_NAME } from '../../../../utils/scheduleRouteForm';

import { useAssistantGenerateRoutesDialogByItemsContext } from '../../../Context';
import {
  FORM_BY_ITEMS_FIELDS_NAME,
  getFormByItemsInitialValues,
  isSubmiTypeGenerateTemplate,
} from '../../../utils';

const AssistantGenerateRoutesDialogByItemsContentStepperTab =
  (): ReactElement => {
    const formatterDateUtcOffset = useUtcOffset();

    const {
      stepper: {
        currentApiTransactionId: { value: currentApiTransactionId },
      },
      actions,
    } = useAssistantGenerateRoutesDialogByItemsContext();

    const { values } =
      useFormikContext<ReturnType<typeof getFormByItemsInitialValues>>();

    const {
      data,
      loadingData,
      activeStep,
      workProgressId,
      indicators,
      loadingIndicators,
      totalStops,
      totalVehicles,
      errors,
    } = useGenerateRouteStepperData({
      apiTransactionId: currentApiTransactionId as string,
      onSuccessComplete: actions?.onStepperComplete,
    });

    const submitType = useMemo(
      () => values[FORM_BY_ITEMS_FIELDS_NAME.SUBMIT_TYPE],
      [values],
    );

    const isScheduleRouteEnabled = useMemo(
      () => values[SCHEDULE_ROUTE_BASE_FORM_FIELDS_NAME.SCHEDULE_ROUTE_ENABLED],
      [values],
    );

    const isTemplate = useMemo(
      () => isSubmiTypeGenerateTemplate(submitType) || isScheduleRouteEnabled,
      [isScheduleRouteEnabled, submitType],
    );

    const title = useMemo(() => {
      const prefix = isTemplate ? 'Plantilla' : 'Plan de viaje';
      const formatDate = isTemplate ? 'DD/MM/YYYY' : 'DD/MM/YYYY HH:mm';

      return `${prefix} ${data?.name ?? ''} (${formatterDateUtcOffset(
        data?.scheduledDateTime,
        formatDate,
      )})`;
    }, [
      data?.name,
      data?.scheduledDateTime,
      formatterDateUtcOffset,
      isTemplate,
    ]);

    const typeStepperData = useMemo(
      () => (isTemplate ? 'generateTemplate' : 'generate'),
      [isTemplate],
    );

    if (loadingData) {
      return (
        <Stack flex={1} justifyContent="center" alignItems="center">
          <CircularProgress color="primary" disableShrink size={60} />
        </Stack>
      );
    }

    return (
      <Stack spacing={2} flex={1}>
        <Typography variant="h6" component="h2" color="text.primary">
          {title}
        </Typography>

        <Stack spacing={2}>
          <StepperGenerateRoute
            activeStep={activeStep}
            workProgressId={workProgressId}
            errors={errors}
            type={typeStepperData}
            showErrors={false}
            light={false}
          />

          {isCompletedWorkProgressId(workProgressId) && (
            <GenerateRouteCompletedTabs
              workProgressId={workProgressId}
              indicators={indicators}
              loadingIndicators={loadingIndicators}
              errors={errors}
              totalStops={totalStops}
              totalVehicles={totalVehicles}
              type={typeStepperData}
            />
          )}
        </Stack>
      </Stack>
    );
  };

export default AssistantGenerateRoutesDialogByItemsContentStepperTab;
